import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  useTheme
} from '@mui/material';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

export interface ContextMenuProps {
  open: boolean;
  position: { x: number; y: number };
  onClose: () => void;
  onCut: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onFillDown: () => void;
  onFillRight: () => void;
  onClearCells: () => void;
  onClearColumn?: () => void;
  onFindReplace: () => void;
  onAutoFill: () => void;
  onSelectInModel?: () => void;
  disabledItems?: {
    cut?: boolean;
    copy?: boolean;
    paste?: boolean;
    fillDown?: boolean;
    fillRight?: boolean;
    clearCells?: boolean;
    autoFill?: boolean;
    selectInModel?: boolean;
  };
  selectedRowCount?: number;
  selectedCellCount?: number;
  activeColumnLabel?: string | null;
  fillDownTargetCount?: number;
  modelSelectionCount?: number;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  open,
  position,
  onClose,
  onCut,
  onCopy,
  onPaste,
  onFillDown,
  onFillRight,
  onClearCells,
  onClearColumn,
  onFindReplace,
  onAutoFill,
  onSelectInModel,
  disabledItems = {},
  selectedRowCount = 0,
  selectedCellCount = 0,
  activeColumnLabel = null,
  fillDownTargetCount,
  modelSelectionCount = 0
}) => {
  const theme = useTheme();
  const resolvedFillDownTargetCount = Math.max(0, fillDownTargetCount ?? selectedRowCount);
  const hasClearSelection = selectedRowCount > 0 || selectedCellCount > 0;
  const hasActiveColumn = Boolean(activeColumnLabel && activeColumnLabel.trim());

  const handleItemClick = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <Menu
      open={open}
      onClose={onClose}
      MenuListProps={{ 'aria-label': 'Table context actions' }}
      anchorReference="anchorPosition"
      anchorPosition={
        position.x !== 0 && position.y !== 0
          ? { top: position.y, left: position.x }
          : undefined
      }
      slotProps={{
        paper: {
          sx: {
            minWidth: 200,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[8],
          }
        }
      }}
    >
      {/* Clipboard Operations */}
      <MenuItem
        onClick={() => handleItemClick(onCut)}
        disabled={disabledItems.cut || selectedRowCount === 0}
      >
        <ListItemIcon>
          <ContentCutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Cut</ListItemText>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Ctrl+X
        </Typography>
      </MenuItem>

      <MenuItem
        onClick={() => handleItemClick(onCopy)}
        disabled={disabledItems.copy || (selectedRowCount === 0 && selectedCellCount === 0)}
      >
        <ListItemIcon>
          <ContentCopyIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Copy</ListItemText>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Ctrl+C
        </Typography>
      </MenuItem>

      <MenuItem
        onClick={() => handleItemClick(onPaste)}
        disabled={disabledItems.paste}
      >
        <ListItemIcon>
          <ContentPasteIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Paste</ListItemText>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Ctrl+V
        </Typography>
      </MenuItem>

      <Divider />

      {/* Fill Operations */}
      <MenuItem
        onClick={() => handleItemClick(onFillDown)}
        disabled={disabledItems.fillDown || resolvedFillDownTargetCount < 2}
      >
        <ListItemIcon>
          <ArrowDownwardIcon fontSize="small" />
        </ListItemIcon>
      <ListItemText>Fill down</ListItemText>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Ctrl+D
        </Typography>
      </MenuItem>

      <MenuItem
        onClick={() => handleItemClick(onFillRight)}
        disabled={disabledItems.fillRight || selectedRowCount === 0}
      >
        <ListItemIcon>
          <ArrowForwardIcon fontSize="small" />
        </ListItemIcon>
      <ListItemText>Fill right</ListItemText>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Ctrl+R
        </Typography>
      </MenuItem>

      <Divider />

      {/* Clear Cells */}
      <MenuItem
        onClick={() => handleItemClick(onClearCells)}
        disabled={disabledItems.clearCells || !hasClearSelection}
      >
        <ListItemIcon>
          <ClearIcon fontSize="small" />
        </ListItemIcon>
      <ListItemText>Clear cells</ListItemText>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Delete
        </Typography>
      </MenuItem>

      <MenuItem
        onClick={() => onClearColumn && handleItemClick(onClearColumn)}
        disabled={!onClearColumn || !hasActiveColumn}
      >
        <ListItemIcon>
          <DeleteSweepIcon fontSize="small" />
        </ListItemIcon>
      <ListItemText>Clear column</ListItemText>
      </MenuItem>

      <Divider />

      {/* Find & Replace */}
      <MenuItem onClick={() => handleItemClick(onFindReplace)}>
        <ListItemIcon>
          <FindReplaceIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Find and replace…</ListItemText>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Ctrl+H
        </Typography>
      </MenuItem>

      {/* Auto-Fill */}
      <MenuItem
        onClick={() => handleItemClick(onAutoFill)}
        disabled={disabledItems.autoFill || selectedRowCount === 0}
      >
        <ListItemIcon>
          <AutoFixHighIcon fontSize="small" color="secondary" />
        </ListItemIcon>
        <ListItemText>Auto-fill selected</ListItemText>
      </MenuItem>

      {onSelectInModel ? (
        <MenuItem
          onClick={() => handleItemClick(onSelectInModel)}
          disabled={disabledItems.selectInModel || modelSelectionCount === 0}
        >
          <ListItemIcon>
            <CenterFocusStrongIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Select component in model</ListItemText>
        </MenuItem>
      ) : null}
    </Menu>
  );
};

export default ContextMenu;
